import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Descriptions, Table, Timeline, Tag, Button, Space, Modal, Input, Alert, Skeleton, Result, Popconfirm, message } from '../../ui';
import { request } from '../../api/client';
import { usePermission, useAuthStore } from '../../store/auth';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { boxLoosePhrase } from '../../lib/packaging';
import { ReceiveReturnModal } from './ReceiveReturnModal';
import { CreditReturnModal } from './CreditReturnModal';
import { CreditNoteDrawer } from '../../components/CreditNoteDrawer';
import { Pill } from '../../components/TwDataTable';

const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CONDITION_COLOR: Record<string, string> = {
  RESALABLE: 'green', NEAR_EXPIRY: 'lime', DAMAGED: 'orange', DEFECTIVE: 'gold', MISMATCH: 'gold', EXPIRED: 'red', BLOCKED: 'volcano', MIXED: 'purple',
};

const STATUS_COLOR: Record<string, string> = {
  REQUESTED: 'blue', APPROVED: 'green', IN_TRANSIT: 'gold', RECEIVED: 'purple',
  CREDITED: 'green', REJECTED: 'red', CANCELLED: 'default', POSTED: 'green',
};
const label = (s?: string) => (s ?? '').replace(/_/g, ' ');

/**
 * The return detail — header + lines + audit trail + approve/reject/withdraw actions. Self-contained (fetches
 * by id) and always shown inside the Returns list's right-side Drawer: both an in-app row click and a
 * deep-link (/returns/:id from the notification bell / activity-log "Open record") open this same drawer, so
 * there is one consistent presentation. `onClosed` lets the drawer close itself after a terminal action
 * (reject/withdraw remove the return from the actor's queue). Also embedded in the Returned-stock page.
 */
export function ReturnDetailPanel({ id, onClosed }: { id: string; onClosed?: () => void }) {
  const qc = useQueryClient();
  const canApprove = usePermission('RETURN.APPROVE');
  const canReceive = usePermission('RETURN.RECEIVE');
  const canCredit = usePermission('CREDIT_NOTE.CREATE');
  const canReverse = usePermission('RETURN.CREDIT.REVERSE');
  const userId = useAuthStore((s) => s.user?.id);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [noteDrawerId, setNoteDrawerId] = useState<string | null>(null);

  const { data: r, isLoading, isError } = useQuery({
    queryKey: ['return', id],
    queryFn: () => request<any>({ url: `/returns/${id}` }),
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['return', id] });
    qc.invalidateQueries({ queryKey: ['returns'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const approve = useMutation({
    mutationFn: () => request({ url: `/returns/${id}/approve`, method: 'POST' }),
    onSuccess: () => { message.success('Return approved'); invalidate(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Approve failed'),
  });
  const reject = useMutation({
    mutationFn: (reason: string) => request({ url: `/returns/${id}/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Return rejected'); setRejectOpen(false); setRejectReason(''); invalidate(); onClosed?.(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Reject failed'),
  });
  const withdraw = useMutation({
    mutationFn: () => request({ url: `/returns/${id}/cancel`, method: 'POST' }),
    onSuccess: () => { message.success('Return withdrawn'); invalidate(); onClosed?.(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Withdraw failed'),
  });
  // WHM: goods are on the way back → in transit; then Receive (a separate modal) records what arrived.
  const markInTransit = useMutation({
    mutationFn: () => request({ url: `/returns/${id}/in-transit`, method: 'POST' }),
    onSuccess: () => { message.success('Marked in transit — ready to receive'); invalidate(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to mark in transit'),
  });
  // Accounts: void a credit note issued in error → the return goes back to RECEIVED, re-creditable. Also
  // refreshes the retailer statement (the compensating debit removed the advance-in-credit).
  const reverse = useMutation({
    mutationFn: (reason: string) => request({ url: `/returns/${id}/reverse-credit`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Credit note reversed'); setReverseOpen(false); setReverseReason(''); invalidate(); qc.invalidateQueries({ queryKey: ['retailer'] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Reverse failed'),
  });

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (isError || !r) return <Result status="404" title="Return not found" />;

  const isRequested = r.status === 'REQUESTED';
  const canWithdraw = isRequested && String(r.requestedBy) === String(userId);
  const isReceivedView = r.status === 'RECEIVED' || r.status === 'CREDITED';

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Space wrap>
          <span className="text-lg font-semibold">{r.returnNo}</span>
          <Tag color={STATUS_COLOR[r.status] ?? 'default'}>{label(r.status)}</Tag>
        </Space>
        <Space wrap>
          {canApprove && isRequested && (
            <>
              <Button variant="danger" onClick={() => setRejectOpen(true)} loading={reject.isPending}>Reject</Button>
              <Button variant="primary" onClick={() => approve.mutate()} loading={approve.isPending}>Approve</Button>
            </>
          )}
          {canReceive && r.status === 'APPROVED' && (
            <Popconfirm
              title="Mark this return as in transit?"
              description="Confirm the goods are on their way back to the warehouse."
              okText="Mark in transit"
              onConfirm={() => markInTransit.mutate()}
            >
              <Button variant="primary" loading={markInTransit.isPending}>Mark in transit</Button>
            </Popconfirm>
          )}
          {canReceive && r.status === 'IN_TRANSIT' && (
            <Button variant="primary" onClick={() => setReceiveOpen(true)}>Receive goods</Button>
          )}
          {canCredit && r.status === 'RECEIVED' && (
            <Button variant="primary" onClick={() => setCreditOpen(true)}>Issue credit note</Button>
          )}
          {canReverse && r.status === 'CREDITED' && (
            <Button variant="danger" onClick={() => setReverseOpen(true)} loading={reverse.isPending}>Reverse credit note</Button>
          )}
          {canWithdraw && <Button variant="danger" onClick={() => withdraw.mutate()} loading={withdraw.isPending}>Withdraw</Button>}
        </Space>
      </div>

      {isRequested && (
        <Alert
          type="info" className="mb-3"
          title="Awaiting approval. Once an admin approves, the warehouse receives + inspects the goods, then Accounts issues the credit note."
        />
      )}
      {canReceive && r.status === 'APPROVED' && (
        <Alert
          type="info" className="mb-3"
          title="Approved. Mark the goods in transit when the retailer ships them back, then receive + inspect them to restock."
        />
      )}
      {canReceive && r.status === 'IN_TRANSIT' && (
        <Alert
          type="warning" className="mb-3"
          title="Goods are in transit. Receive them to record what actually arrived and its condition — resalable units re-enter saleable stock."
        />
      )}
      {canCredit && r.status === 'RECEIVED' && (
        <Alert
          type="info" className="mb-3"
          title="Received + inspected. Issue the credit note — the credit posts to the retailer's advance (credit) balance; there is no cash refund."
        />
      )}
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Order">{r.orderId ? <Link to={`/orders/${r.orderId}`}>{r.orderNo ?? r.orderId}</Link> : '—'}</Descriptions.Item>
        {/* Order-level returns can span several invoices → no single "against invoice"; the credit note(s)
            attach per invoice at the Accounts step. Only show it for a legacy single-invoice return. */}
        {r.invoiceNo && <Descriptions.Item label="Against invoice">{r.invoiceNo}</Descriptions.Item>}
        <Descriptions.Item label="Retailer">{r.companyName ?? `#${r.retailerId}`}{r.retailerCode ? ` (${r.retailerCode})` : ''}</Descriptions.Item>
        <Descriptions.Item label="Reason">
          <span className="capitalize">{label(r.reasonCode)?.toLowerCase() || '—'}</span>
          {r.reasonNote ? <div className="text-xs text-subtle">{r.reasonNote}</div> : null}
        </Descriptions.Item>
        <Descriptions.Item label="Raised by">{r.requestedByName ?? (r.requestedBy ? `#${r.requestedBy}` : '—')}</Descriptions.Item>
        <Descriptions.Item label="Return date">{r.returnDate ? String(r.returnDate).substring(0, 10) : '—'}</Descriptions.Item>
        {Array.isArray(r.proofUrls) && r.proofUrls.length > 0 && (
          <Descriptions.Item label="Proof" span={2}>
            <div className="flex flex-wrap items-center gap-2">
              {r.proofUrls.map((u: string, i: number) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" title="Open proof" className="inline-flex">
                  {/\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(u)
                    ? <img src={u} alt={`proof ${i + 1}`} className="h-14 w-14 rounded border border-line object-cover" />
                    : <span className="flex h-14 w-14 items-center justify-center rounded border border-line bg-surface-2 text-[11px] font-medium text-muted">File {i + 1}</span>}
                </a>
              ))}
            </div>
          </Descriptions.Item>
        )}
        {r.approvedByName || r.approvedBy ? (
          <Descriptions.Item label="Approved by" span={2}>{r.approvedByName ?? `#${r.approvedBy}`}{r.approvedAt ? ` · ${formatIst(r.approvedAt)}` : ''}</Descriptions.Item>
        ) : null}
        {r.creditedByName || r.creditedBy ? (
          <Descriptions.Item label="Credited by" span={2}>{r.creditedByName ?? `#${r.creditedBy}`}{r.creditedAt ? ` · ${formatIst(r.creditedAt)}` : ''}</Descriptions.Item>
        ) : null}
        {r.reversedByName || r.reversedBy ? (
          <Descriptions.Item label="Credit reversed by" span={2}>
            {r.reversedByName ?? `#${r.reversedBy}`}{r.reversedAt ? ` · ${formatIst(r.reversedAt)}` : ''}
            {r.reversalReason ? <div className="text-xs text-subtle">{r.reversalReason}</div> : null}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      <div className="mt-4 mb-1 text-sm font-semibold text-content">Lines</div>
      <Table
        size="small"
        pagination={false}
        rowKey={(l: any) => String(l.id)}
        dataSource={r.lines ?? []}
        locale={{ emptyText: 'No lines' }}
        // After receipt, expand a line to see its per-batch/per-condition split (each sub-lot the WHM recorded).
        expandable={isReceivedView ? {
          rowExpandable: (l: any) => (l.receipts?.length ?? 0) > 1,
          expandedRowRender: (l: any) => (
            <div className="pl-1">
              {(l.receipts ?? []).map((rc: any, i: number) => (
                <div key={i} className="flex items-center gap-2 py-0.5 text-xs">
                  <span className="w-16 text-right font-medium">{fmtQty(rc.qty)}</span>
                  <span className="w-32">{rc.receivedBatchNo || 'auto lot'}</span>
                  <Tag color={CONDITION_COLOR[rc.condition] ?? 'default'}>{label(rc.condition)}</Tag>
                </div>
              ))}
            </div>
          ),
        } : undefined}
        columns={[
          { title: 'Product', dataIndex: 'productName', render: (v: string, l: any) => v || `#${l.productId}` },
          { title: 'HSN', dataIndex: 'hsnCode', width: 100, render: (v: string) => v || '—' },
          { title: 'Requested qty', dataIndex: 'requestedQty', align: 'right', width: 150, render: (v: any, r: any) => {
            const phrase = boxLoosePhrase(r.boxQuantity, r.looseUnitQuantity, r.unitsPerBoxSnapshot);
            return phrase ? <span>{phrase}<div className="text-[11px] text-subtle">= {fmtQty(v)} units</div></span> : fmtQty(v);
          } },
          // Once the warehouse has received, show what actually arrived, the batch it came back in + its condition.
          // A split line reads 'MIXED' / 'multiple' here; expand the row for the per-sub-lot breakdown.
          ...(isReceivedView ? [
            { title: 'Received qty', dataIndex: 'receivedQty', align: 'right' as const, width: 120, render: (v: any) => fmtQty(v ?? 0) },
            { title: 'Batch', dataIndex: 'receivedBatchNo', width: 120, render: (v: string, l: any) => v || ((l.receipts?.length ?? 0) > 1 ? 'multiple' : '—') },
            { title: 'Condition', dataIndex: 'condition', width: 130, render: (v: string) => v ? <Tag color={CONDITION_COLOR[v] ?? 'default'}>{label(v)}</Tag> : '—' },
          ] : []),
        ]}
      />

      {(r.creditNotes?.length ?? 0) > 0 && (
        <>
          <div className="mt-4 mb-1 text-sm font-semibold text-content">Credit notes</div>
          <Table
            size="small"
            pagination={false}
            rowKey={(cn: any) => String(cn.noteNo)}
            dataSource={r.creditNotes ?? []}
            columns={[
              // The note number opens the canonical credit-note view (links + reversal + ledger trail).
              { title: 'Credit note', dataIndex: 'noteNo', render: (v: string, cn: any) => (
                <button className="font-medium text-brand-700 hover:underline dark:text-brand-400" onClick={() => setNoteDrawerId(String(cn.id))}>{v}</button>
              ) },
              { title: 'Against invoice', dataIndex: 'invoiceNo', render: (v: string) => v || '—' },
              { title: 'Status', dataIndex: 'status', width: 110, render: (v: string) => v === 'CANCELLED' ? <Pill color="red">Reversed</Pill> : <Pill color="green">Issued</Pill> },
              { title: 'Taxable', dataIndex: 'taxableAmt', align: 'right', width: 110, render: (v: any) => inr2(v) },
              { title: 'GST', align: 'right', width: 110, render: (_: any, cn: any) => inr2(Number(cn.cgstAmt) + Number(cn.sgstAmt) + Number(cn.igstAmt)) },
              // A reversed note's amount is struck through — it no longer contributes to the advance-in-credit.
              { title: 'Amount', dataIndex: 'amount', align: 'right', width: 120, render: (v: any, cn: any) => (
                <span className={cn.status === 'CANCELLED' ? 'text-subtle line-through' : 'font-semibold'}>{inr2(v)}</span>
              ) },
            ]}
          />
        </>
      )}

      <div className="mt-4 mb-2 text-sm font-semibold text-content">Audit trail</div>
      <Timeline
        items={(r.history ?? []).map((h: any) => ({
          color: STATUS_COLOR[h.toStatus] ?? 'gray',
          children: (
            <div>
              <span className="font-medium">{h.fromStatus ? `${label(h.fromStatus)} → ` : ''}{label(h.toStatus)}</span>
              <span className="ml-2 text-xs text-subtle">{formatIst(h.createdAt)}{h.changedByName ? ` · ${h.changedByName}` : ''}</span>
              {h.notes ? <div className="text-xs text-muted">{h.notes}</div> : null}
            </div>
          ),
        }))}
      />

      <Modal
        open={rejectOpen}
        title="Reject this return"
        onOpenChange={(o) => { if (!o) setRejectOpen(false); }}
        footer={<>
          <Button variant="default" onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button variant="danger" loading={reject.isPending} disabled={!rejectReason.trim()} onClick={() => reject.mutate(rejectReason.trim())}>Reject</Button>
        </>}
      >
        <Input.TextArea rows={3} maxLength={255} placeholder="Reason for rejection (required — the requester will see this)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
      </Modal>

      <ReceiveReturnModal
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        returnId={id}
        warehouseId={String(r.warehouseId ?? '')}
        lines={r.lines ?? []}
      />

      <CreditReturnModal open={creditOpen} onClose={() => setCreditOpen(false)} returnId={id} />

      <Modal
        open={reverseOpen}
        title="Reverse credit note"
        onOpenChange={(o) => { if (!o) setReverseOpen(false); }}
        footer={<>
          <Button variant="default" onClick={() => setReverseOpen(false)}>Cancel</Button>
          <Button variant="danger" loading={reverse.isPending} disabled={!reverseReason.trim()} onClick={() => reverse.mutate(reverseReason.trim())}>Reverse credit note</Button>
        </>}
      >
        <div className="mb-3 rounded-control border border-danger-600/25 bg-danger-600/10 px-3 py-2 text-xs text-content">
          This voids the credit note(s) issued for this return and posts a compensating debit — the retailer's
          advance-in-credit is <span className="font-semibold">removed</span> and the return returns to
          <span className="font-semibold"> Received</span> so a corrected credit note can be re-issued. No stock moves.
        </div>
        <Input.TextArea rows={3} maxLength={255} placeholder="Reason for reversal (required — recorded on the audit trail)" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
      </Modal>

      <CreditNoteDrawer id={noteDrawerId} onClose={() => setNoteDrawerId(null)} />
    </div>
  );
}
